const DAY_MS = 24 * 60 * 60 * 1000;

export interface TimeValuePoint {
  timestamp: number;
  value: number;
}

/**
 * Transform a daily series into a trailing-window sum series (e.g. rolling
 * 30-day spend), so a daily metric can be charted on the same basis as a
 * monthly baseline/target.
 *
 * For each input point, the output value is the sum of all points whose
 * timestamp falls in the trailing `windowDays` calendar window ending at that
 * point (inclusive). Points whose window reaches back before the fetched
 * range are dropped rather than emitted as undercounts — callers should
 * over-fetch by `windowDays` (see useMetricHistory's `extraDays`) so the
 * displayed span keeps its full width.
 *
 * `coverageStart` is where the fetched range begins. With it, a window that
 * is fully inside the fetch but reaches back before the metric's first stored
 * day is a real (partial) total, not an undercount, and is emitted — the same
 * basis as the dashboard headline, which sums whatever history exists. This
 * is what lets a young metric chart at all: without it a 30-day rolling chart
 * of a metric with a week of history draws nothing for three weeks. Without
 * `coverageStart` the earliest point stands in for it, which keeps the strict
 * behaviour.
 *
 * Input order doesn't matter — points are sorted by timestamp internally.
 */
export function rollingWindowSum(
  points: TimeValuePoint[],
  windowDays: number,
  coverageStart?: number,
): TimeValuePoint[] {
  if (points.length === 0 || windowDays <= 0) return [];

  // Sort defensively rather than contracting "must be sorted" — an unsorted
  // input would otherwise produce silently wrong sums, and n is small.
  points = [...points].sort((a, b) => a.timestamp - b.timestamp);

  const windowMs = windowDays * DAY_MS;
  // Never later than the first point: a fetch cannot start after data it returned.
  const earliest = Math.min(points[0].timestamp, coverageStart ?? Infinity);
  const result: TimeValuePoint[] = [];

  let windowStartIdx = 0;
  let windowSum = 0;
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    windowSum += point.value;
    const windowFloor = point.timestamp - windowMs;
    while (points[windowStartIdx].timestamp <= windowFloor) {
      windowSum -= points[windowStartIdx].value;
      windowStartIdx++;
    }
    // Only emit points whose full trailing window is inside the fetched
    // range; earlier points would be silent undercounts.
    if (windowFloor + DAY_MS >= earliest) {
      result.push({
        timestamp: point.timestamp,
        value: Math.round(windowSum * 100) / 100,
      });
    }
  }
  return result;
}
